import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from '../index'

export interface ChannelInfo {
    channelNum: number
    isActive: number
    mode: number
    pwmDuty: number
    syrupId: number
    channelName?: string
    calibrationStatus: number
    calibrationDurationMs: number
}

export interface ChannelListResponse {
    channelCount: number
    success: number
    channels: ChannelInfo[]
}

export interface ChannelConfigResponse {
    success: number
    channelNum: number
    isActive: number
    mode: number
    pwmDuty: number
    syrupId: number
    syrupName: string
    channelName?: string
    calibrationStatus: number
    calibrationDurationMs: number
    errorMsg?: string
}

export interface ChannelUpdateRequest {
    channelNum: number
    isActive: number
    mode: number
    pwmDuty: number
    syrupId: number
    calibrationDurationMs: number
    channelName?: string
}

export interface ChannelUpdateResponse {
    channelNum: number
    success: number
    errorMsg: string
}

export interface ChannelManualControlRequest {
    channelNum: number
    action: number
    pwmValue?: number
}

export interface ChannelManualControlResponse {
    channelNum: number
    success: number
    errorMsg: string
}

export interface AllChannelsControlRequest {
    action: number
}

export interface AllChannelsControlResponse {
    success: number
    channelsAffected: number
    errorMsg: string
}

export interface ChannelCalibrateRequest {
    channelNum: number
    testVolumeMl?: number
}

export interface ChannelCalibrateResponse {
    channelNum: number
    success: number
    errorMsg: string
}

export async function getChannelList(clientId: string): Promise<ApiResponse<ChannelListResponse>> {
    const response = await fetch(`${HOST_URL}channels/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as ApiResponse<ChannelListResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getChannelConfig(clientId: string, channelNum: number): Promise<ApiResponse<ChannelConfigResponse>> {
    const response = await fetch(`${HOST_URL}channels/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, channelNum }),
    })
    const result = (await response.json()) as ApiResponse<ChannelConfigResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function updateChannel(clientId: string, data: ChannelUpdateRequest): Promise<ApiResponse<ChannelUpdateResponse>> {
    const response = await fetch(`${HOST_URL}channels/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...data }),
    })
    const result = (await response.json()) as ApiResponse<ChannelUpdateResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function manualControl(clientId: string, data: ChannelManualControlRequest): Promise<ApiResponse<ChannelManualControlResponse>> {
    const response = await fetch(`${HOST_URL}channels/manual-control`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...data }),
    })
    const result = (await response.json()) as ApiResponse<ChannelManualControlResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function allChannelsControl(clientId: string, data: AllChannelsControlRequest): Promise<ApiResponse<AllChannelsControlResponse>> {
    const response = await fetch(`${HOST_URL}channels/all-control`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...data }),
    })
    const result = (await response.json()) as ApiResponse<AllChannelsControlResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function calibrateChannel(clientId: string, data: ChannelCalibrateRequest): Promise<ApiResponse<ChannelCalibrateResponse>> {
    const response = await fetch(`${HOST_URL}channels/calibrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...data }),
    })
    const result = (await response.json()) as ApiResponse<ChannelCalibrateResponse>
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}
